// store/slices/authSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../services/authService";

export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  kyc_status: string | null;
  email_verified: boolean;
  xrp_balance: number;
  is_active?: boolean;
  created_at?: string;
  country?: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authChecked: boolean;
  error: string | null;
  pendingVerificationEmail: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("token"),
  isAuthenticated: false,
  isLoading: true,
  authChecked: false,
  error: null,
  pendingVerificationEmail: null,
};

export const login = createAsyncThunk(
  "auth/login",
  async (
    {
      email_or_username,
      password,
    }: { email_or_username: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await authService.login(email_or_username, password);
      return response;
    } catch (error: any) {
      if (error.isEmailVerificationRequired) {
        return rejectWithValue({
          type: "email_verification_required",
          email: error.email,
          message: error.message,
        });
      }
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

export const register = createAsyncThunk(
  "auth/register",
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      console.log("Registration service response:", response);
      return response;
    } catch (error: any) {
      console.error("Registration service error:", error);
      return rejectWithValue(
        error.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const logout = createAsyncThunk("auth/logout", async () => {
  await authService.logout();
});

export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return null;
      }

      const response = await authService.checkAuth();
      return response;
    } catch (error: any) {
      console.error("Check auth error:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
      }
      return rejectWithValue(
        error.response?.data?.message || "Not authenticated",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.authChecked = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.authChecked = true;
      state.error = null;
      state.pendingVerificationEmail = null;
    },
    setPendingVerificationEmail: (state, action) => {
      state.pendingVerificationEmail = action.payload;
    },
    clearPendingVerificationEmail: (state) => {
      state.pendingVerificationEmail = null;
    },
    setAuthChecked: (state) => {
      state.authChecked = true;
      state.isLoading = false;
    },
    // Allow updating user country in auth state after profile update
    updateUserCountry: (state, action) => {
      if (state.user) {
        state.user.country = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.authChecked = true;

        console.log("Login fulfilled payload:", action.payload);

        if (action.payload.requiresEmailVerification) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          state.pendingVerificationEmail = action.payload.email;
          return;
        }

        if (action.payload.access_token) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.access_token;
          state.pendingVerificationEmail = null;
          localStorage.setItem("token", action.payload.access_token);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.authChecked = true;

        const payload = action.payload as any;

        if (
          payload?.type === "email_verification_required" ||
          payload?.isEmailVerificationRequired
        ) {
          state.pendingVerificationEmail = payload.email;
          state.error = payload.message || "Email verification required";
        } else {
          state.error =
            typeof action.payload === "string"
              ? action.payload
              : "Login failed. Please try again.";
        }
      })

      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.authChecked = true;
        state.pendingVerificationEmail = action.payload.user?.email || null;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.authChecked = true;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.authChecked = true;
        state.pendingVerificationEmail = null;
      })

      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.authChecked = true;

        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.pendingVerificationEmail = null;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
        }
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.authChecked = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.pendingVerificationEmail = null;
      });
  },
});

export const {
  setCredentials,
  clearError,
  clearAuthState,
  setPendingVerificationEmail,
  clearPendingVerificationEmail,
  setAuthChecked,
  updateUserCountry,
} = authSlice.actions;
export default authSlice.reducer;
